import {
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  Typography,
  useEventCallback,
} from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { ReactElement, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DocumentCivilUnionUploadFormVM } from 'civil-status/view-models/document-birth-form';
import MatricolaList from '../shared/matricola-list';
import ListGender from 'shell/components/shared/gender';
import { DatePicker } from '@mui/lab';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { HttpResponse } from 'shell/models/http-response';
import { ConvertedImage } from 'shell/models/convert-image';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { Cropper } from 'shell/components/shared/cropper';
import SelectSerie from '../shared/select-serie';
import SelectRegistry from '../shared/select-registry';
import * as yup from 'yup';
import { commonSchemaObject } from 'civil-status/constants/common-schema';
import { civilUnionSchemaObject } from '../../../shell/constants/create-schema-civil-union';

const validationSchema = yup.object().shape({
  ...civilUnionSchemaObject,
  ...commonSchemaObject,
});

interface FormDocumentCivilUnionUploadNewProps {
  onOk: (form: DocumentCivilUnionUploadFormVM) => void;
  onCancel: (form?: DocumentCivilUnionUploadFormVM) => void;
  title: string;
  children?: ReactElement;
  disabled?: boolean;
  isEdit?: boolean;
}

const FormDocumentCivilUnionUploadNew = ({
  onOk,
  onCancel,
  title,
  disabled = false,
}: FormDocumentCivilUnionUploadNewProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const form = useFormik<DocumentCivilUnionUploadFormVM>({
    initialValues: {
      year: '',
      registerNumber: '',
      documentNumber: '',
      portion: '',
      serie: '',
      serie2: '',
      matricola: '',
      matricola2: '',
      surname: '',
      surname2: '',
      name: '',
      yearsDeclarant1: '',
      yearDateDeclarant1: '',
      yearDateDeclarant2: '',
      yearsDeclarant2: '',
      name2: '',
      gender: '',
      gender2: '',
      municipalityBirth: '',
      municipalityBirth2: '',
      time: null,
      time2: null,
      birthday: null,
      birthday2: null,
      nameFather: '',
      nameFather2: '',
      surnameFather: '',
      surnameFather2: '',
      nameMother: '',
      nameMother2: '',
      civilUnionDate: null,
      martitalStatusDeclarant1: '',
      martitalStatusDeclarant2: '',
      civilUnionMunicipality: '',
      surnameMother: '',
      surnameMother2: '',
      residenceMunicipalityDeclarant1: '',
      residenceMunicipalityDeclarant2: '',
      surnamePostUnionDeclarant1: '',
      surnamePostUnionDeclarant2: '',
      citizenshipDeclarant1: '',
      citizenshipDeclarant2: '',
    },
    onSubmit,
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
  });

  const convertDocument = useHttpClient<HttpResponse<ConvertedImage>>(
    `${__CIVIL_STATUS_API_URL__}/convert-image`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
    }
  );

  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};
      (
        convertDocument({
          data: {
            formato: 'png',
            immagineIniziale: (result as string)?.split(',')[1],
          },
        }) as ResponsePromise<HttpResponse<ConvertedImage>>
      ).then((response) => {
        form.setFieldValue('upload', {
          file,
          raw: response?.data?.payload?.immagineConvertita
            ? (response?.data?.payload?.immagineConvertita as Array<string>)?.map(
                (immagine: string) => `data:image/png;base64,${immagine}`
              )
            : [],
          rawTiff: result as string,
        });
      });
    });

    reader.readAsDataURL(file);
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={form.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Anno"
                    aria-label="Anno"
                    id="year"
                    fullWidth
                    inputProps={{
                      maxLength: '4',
                    }}
                    onChange={(event) => {
                      form.setFieldValue('year', event?.target.value);
                    }}
                    value={form.values.year || ''}
                    helperText={form.errors.year}
                    error={form.touched.year && Boolean(form.errors.year)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Atto"
                    aria-label="Atto"
                    fullWidth
                    id="document"
                    onChange={(event) => {
                      form.setFieldValue('documentNumber', event?.target.value);
                    }}
                    inputProps={{
                      maxLength: '4',
                    }}
                    value={form.values.documentNumber || ''}
                    helperText={form.errors.documentNumber}
                    error={form.touched.documentNumber && Boolean(form.errors.documentNumber)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <SelectRegistry
                    isSmallSize
                    label="Registro"
                    id="registry"
                    onChange={(event) => {
                      form.setFieldValue('registerNumber', event?.target.value);
                    }}
                    value={form.values.registerNumber || ''}
                    error={form.touched.registerNumber && Boolean(form.errors.registerNumber)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Parte"
                    aria-label="Parte"
                    fullWidth
                    id="portion"
                    onChange={(event) => {
                      form.setFieldValue('portion', event?.target.value);
                    }}
                    inputProps={{
                      maxLength: '2',
                    }}
                    value={form.values.portion || ''}
                    helperText={form.errors.portion}
                    error={form.touched.portion && Boolean(form.errors.portion)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <SelectSerie
                    isSmallSize
                    label="Serie"
                    id="serie"
                    onChange={(event) => {
                      form.setFieldValue('serie', event?.target.value);
                    }}
                    value={form.values.serie || ''}
                    error={form.touched.serie && Boolean(form.errors.serie)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }} md={4}>
                <DatePicker
                  label="Data Unione Civile *"
                  aria-label="Data Unione Civile"
                  value={form.values?.civilUnionDate || null}
                  disabled={disabled}
                  onChange={(value) => form.setFieldValue('civilUnionDate', value)}
                  renderInput={(params) => (
                    <PmiTextField
                      id="civilUnionDate"
                      name="civilUnionDate"
                      {...params}
                      fullWidth
                      error={form.touched?.civilUnionDate && Boolean(form.errors?.civilUnionDate)}
                      helperText={form.errors?.civilUnionDate}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }} md={8}>
                <PmiTextField
                  aria-label="Comune Unione Civile"
                  label="Comune Unione Civile *"
                  id="civilUnionMunicipality"
                  name="civilUnionMunicipality"
                  value={form.values?.civilUnionMunicipality || ''}
                  helperText={form.errors?.civilUnionMunicipality}
                  onKeyDown={(event) => {
                    if (event.code === 'F2') {
                      event.preventDefault();
                      event.stopPropagation();
                      form.setFieldValue('civilUnionMunicipality', 'MILANO');
                      return;
                    }
                  }}
                  onChange={(event) => {
                    form.setFieldValue('civilUnionMunicipality', event?.target.value);
                  }}
                  error={
                    form.touched?.civilUnionMunicipality &&
                    Boolean(form.errors?.civilUnionMunicipality)
                  }
                  fullWidth
                  disabled={disabled}
                  inputProps={{
                    maxLength: '155',
                  }}
                />
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <MatricolaList
                  aria-label="Matricola"
                  label="Matricola"
                  id="matricola"
                  disabled={disabled}
                  surname={form.values?.surname || ''}
                  name={form.values?.name || ''}
                  value={form.values?.matricola || ''}
                  dateBirth={form.values?.birthday || null}
                  onChanges={(value) => form.setValues({ ...form.values, ...value })}
                  error={form.touched?.matricola && Boolean(form.errors?.matricola)}
                  isSmallSize
                />
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Cognome"
                    label="Cognome *"
                    id="surname"
                    name="surname"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.surname || ''}
                    onChange={(event) => form.setFieldValue('surname', event?.target.value)}
                    error={form.touched?.surname && Boolean(form.errors?.surname)}
                    helperText={form.errors?.surname}
                    inputProps={{
                      maxLength: '155',
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Nome"
                    label="Nome *"
                    id="name"
                    name="name"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.name || ''}
                    onChange={(event) => form.setFieldValue('name', event?.target.value)}
                    error={form.touched?.name && Boolean(form.errors?.name)}
                    helperText={form.errors?.name}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={3}>
                  <ListGender
                    value={form.values?.gender || ''}
                    onChange={(event) => form.setFieldValue('gender', event?.target.value)}
                    error={form.touched?.gender && Boolean(form.errors?.gender)}
                    disabled={disabled}
                    isSmallSize
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={4}>
                  <DatePicker
                    label="Data nascita"
                    aria-label="Data nascita"
                    value={form.values?.birthday || null}
                    disabled={disabled}
                    onChange={(value) => form.setFieldValue('birthday', value)}
                    renderInput={(params) => (
                      <PmiTextField
                        id="birthday"
                        name="birthday"
                        {...params}
                        fullWidth
                        error={form.touched?.birthday && Boolean(form.errors?.birthday)}
                        helperText={form.errors?.birthday}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={2}>
                  <PmiTextField
                    aria-label="Anni"
                    label="Anni"
                    id="yearsDeclarant1"
                    name="yearsDeclarant1"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.yearsDeclarant1 || ''}
                    onChange={(event) => form.setFieldValue('yearsDeclarant1', event?.target.value)}
                    error={form.touched?.yearsDeclarant1 && Boolean(form.errors?.yearsDeclarant1)}
                    helperText={form.errors?.yearsDeclarant1}
                    inputProps={{
                      maxLength: '2',
                    }}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={3}>
                  <PmiTextField
                    aria-label="Anno Nascita"
                    label="Anno Nascita"
                    id="yearDateDeclarant1"
                    name="yearDateDeclarant1"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.yearDateDeclarant1 || ''}
                    onChange={(event) =>
                      form.setFieldValue('yearDateDeclarant1', event?.target.value)
                    }
                    error={
                      form.touched?.yearDateDeclarant1 && Boolean(form.errors?.yearDateDeclarant1)
                    }
                    helperText={form.errors?.yearDateDeclarant1}
                    inputProps={{
                      maxLength: '4',
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune nascita"
                    label="Comune nascita"
                    id="municipalityBirth"
                    name="municipalityBirth"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.municipalityBirth || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('municipalityBirth', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('municipalityBirth', event?.target.value);
                    }}
                    error={
                      form.touched?.municipalityBirth && Boolean(form.errors?.municipalityBirth)
                    }
                    helperText={form.errors?.municipalityBirth}
                    inputProps={{
                      maxLength: '155',
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} md={9} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune residenza"
                    label="Comune residenza"
                    id="residenceMunicipalityDeclarant1"
                    name="residenceMunicipalityDeclarant1"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.residenceMunicipalityDeclarant1 || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('residenceMunicipalityDeclarant1', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('residenceMunicipalityDeclarant1', event?.target.value);
                    }}
                    error={
                      form.touched?.residenceMunicipalityDeclarant1 &&
                      Boolean(form.errors?.residenceMunicipalityDeclarant1)
                    }
                    helperText={form.errors?.residenceMunicipalityDeclarant1}
                    inputProps={{
                      maxLength: '155',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3} style={{ paddingTop: '10px' }}>
                  <span>F2 per Milano</span>
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={7}>
                  <PmiTextField
                    label="Cittadinanza"
                    aria-label="Cittadinanza"
                    id="citizenship-1"
                    name="citizenship-1"
                    fullWidth
                    value={form.values?.citizenshipDeclarant1 || ''}
                    disabled={disabled}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('citizenshipDeclarant1', 'ITALIANA');
                        return;
                      }
                    }}
                    onChange={(event) =>
                      form.setFieldValue('citizenshipDeclarant1', event.target.value)
                    }
                    error={
                      form.touched?.citizenshipDeclarant1 &&
                      Boolean(form.errors?.citizenshipDeclarant1)
                    }
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Cognome post unione"
                    label="Cognome post unione"
                    id="surnamePostUnionDeclarant1"
                    name="surnamePostUnionDeclarant1"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.surnamePostUnionDeclarant1 || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('surnamePostUnionDeclarant1', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('surnamePostUnionDeclarant1', event?.target.value);
                    }}
                    error={
                      form.touched?.surnamePostUnionDeclarant1 &&
                      Boolean(form.errors?.surnamePostUnionDeclarant1)
                    }
                    helperText={form.errors?.surnamePostUnionDeclarant1}
                    inputProps={{
                      maxLength: '155',
                    }}
                  />
                </Grid>
              </Grid>
              {/* Cittadino 2 */}
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <span>Secondo dichiarante </span>
                  <Divider />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <MatricolaList
                    aria-label="Matricola"
                    label="Matricola"
                    id="matricola2"
                    surname={form.values?.surname2 || ''}
                    name={form.values?.name2 || ''}
                    dateBirth={form.values?.birthday2 || null}
                    disabled={disabled}
                    value={form.values?.matricola2 || ''}
                    onChanges={(value) =>
                      form.setValues({
                        ...form.values,
                        matricola2: value.matricola,
                        birthday2: value.birthday,
                        gender2: value.gender,
                        name2: value.name,
                        surname2: value.surname,
                      })
                    }
                    error={form.touched?.matricola2 && Boolean(form.errors?.matricola2)}
                    isSmallSize
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Cognome"
                    label="Cognome *"
                    id="surname2"
                    name="surname2"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.surname2 || ''}
                    onChange={(event) => form.setFieldValue('surname2', event?.target.value)}
                    error={form.touched?.surname2 && Boolean(form.errors?.surname2)}
                    helperText={form.errors?.surname2}
                    inputProps={{
                      maxLength: '155',
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Nome"
                    label="Nome *"
                    id="name2"
                    name="name2"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.name2 || ''}
                    onChange={(event) => form.setFieldValue('name2', event?.target.value)}
                    error={form.touched?.name2 && Boolean(form.errors?.name2)}
                    helperText={form.errors?.name2}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={3}>
                  <ListGender
                    value={form.values?.gender2 || ''}
                    onChange={(event) => form.setFieldValue('gender2', event?.target.value)}
                    error={form.touched?.gender2 && Boolean(form.errors?.gender2)}
                    disabled={disabled}
                    isSmallSize
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={4}>
                  <DatePicker
                    label="Data nascita"
                    aria-label="Data nascita"
                    value={form.values?.birthday2 || null}
                    disabled={disabled}
                    onChange={(value) => form.setFieldValue('birthday2', value)}
                    renderInput={(params) => (
                      <PmiTextField
                        id="birthday2"
                        name="birthday2"
                        {...params}
                        fullWidth
                        error={form.touched?.birthday2 && Boolean(form.errors?.birthday2)}
                        helperText={form.errors?.birthday2}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={2}>
                  <PmiTextField
                    aria-label="Anni"
                    label="Anni"
                    id="yearsDeclarant2"
                    name="yearsDeclarant2"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.yearsDeclarant2 || ''}
                    onChange={(event) => form.setFieldValue('yearsDeclarant2', event?.target.value)}
                    error={form.touched?.yearsDeclarant2 && Boolean(form.errors?.yearsDeclarant2)}
                    helperText={form.errors?.yearsDeclarant2}
                    inputProps={{
                      maxLength: '2',
                    }}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={3}>
                  <PmiTextField
                    aria-label="Anno Nascita"
                    label="Anno Nascita"
                    id="yearDateDeclarant2"
                    name="yearDateDeclarant2"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.yearDateDeclarant2 || ''}
                    onChange={(event) =>
                      form.setFieldValue('yearDateDeclarant2', event?.target.value)
                    }
                    error={
                      form.touched?.yearDateDeclarant2 && Boolean(form.errors?.yearDateDeclarant2)
                    }
                    helperText={form.errors?.yearDateDeclarant2}
                    inputProps={{
                      maxLength: '4',
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune nascita"
                    label="Comune nascita"
                    id="municipalityBirth2"
                    name="municipalityBirth2"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.municipalityBirth2 || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('municipalityBirth2', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('municipalityBirth2', event?.target.value);
                    }}
                    error={
                      form.touched?.municipalityBirth2 && Boolean(form.errors?.municipalityBirth2)
                    }
                    helperText={form.errors?.municipalityBirth}
                    inputProps={{
                      maxLength: '155',
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} md={9} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune residenza"
                    label="Comune residenza"
                    id="residenceMunicipalityDeclarant2"
                    name="residenceMunicipalityDeclarant2"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.residenceMunicipalityDeclarant2 || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('residenceMunicipalityDeclarant2', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('residenceMunicipalityDeclarant2', event?.target.value);
                    }}
                    error={
                      form.touched?.residenceMunicipalityDeclarant2 &&
                      Boolean(form.errors?.residenceMunicipalityDeclarant2)
                    }
                    helperText={form.errors?.residenceMunicipalityDeclarant2}
                    inputProps={{
                      maxLength: '155',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3} style={{ paddingTop: '10px' }}>
                  <span>F2 per Milano</span>
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={7}>
                  <PmiTextField
                    label="Cittadinanza"
                    aria-label="Cittadinanza"
                    id="citizenship-2"
                    name="citizenship-2"
                    fullWidth
                    value={form.values?.citizenshipDeclarant2 || ''}
                    disabled={disabled}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('citizenshipDeclarant2', 'ITALIANA');
                        return;
                      }
                    }}
                    onChange={(event) =>
                      form.setFieldValue('citizenshipDeclarant2', event.target.value)
                    }
                    error={
                      form.touched?.citizenshipDeclarant2 &&
                      Boolean(form.errors?.citizenshipDeclarant2)
                    }
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Cognome post unione"
                    label="Cognome post unione"
                    id="surnamePostUnionDeclarant2"
                    name="surnamePostUnionDeclarant2"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.surnamePostUnionDeclarant2 || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('surnamePostUnionDeclarant2', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('surnamePostUnionDeclarant2', event?.target.value);
                    }}
                    error={
                      form.touched?.surnamePostUnionDeclarant2 &&
                      Boolean(form.errors?.surnamePostUnionDeclarant2)
                    }
                    helperText={form.errors?.surnamePostUnionDeclarant2}
                    inputProps={{
                      maxLength: '155',
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                Carica documento&nbsp;
                <PmiInputFile
                  accept="image/tiff"
                  multiple={false}
                  file={form.values.upload?.file}
                  showFilename
                  onChange={handleAddFile}
                  color="cdmBrandThird"
                  tipMaxSize={300}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} md={8}>
              <StickyDiv>
                <Cropper
                  images={form.values.upload?.raw || []}
                  disabled={!form.values?.upload?.raw?.length}
                />
              </StickyDiv>
            </Grid>
          </Grid>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(form.values);
            }}
          >
            Annulla
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormDocumentCivilUnionUploadNew.displayName = 'Form Document Birth new component';

export default FormDocumentCivilUnionUploadNew;
